import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Form, Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import FormButton from "../common/FormButton";
import FormInput from "../common/FormInput";
import LoginFormLogo from "../login/components/LoginFormLogo";
import LinkedMessage from "../universal-login/LinkedMessage";
import { useHandleForgotPasswordSubmit } from "./services";
import { InitialValues } from "./types";

type ValidationSchema = (t: Function) => Yup.ObjectSchema<{
  email: string;
}>;
const validationSchema: ValidationSchema = (t) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t("error.validation.email.invalid"))
      .required(t("error.validation.email.required")),
  });

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const { handleForgotPasswordSubmit, isCoolingDown } =
    useHandleForgotPasswordSubmit();

  const initialValues: InitialValues = {
    email: "",
    password: "",
    apiResponse: "",
  };

  return (
    <Formik
      validationSchema={validationSchema(t)}
      validateOnMount={false}
      initialValues={initialValues}
      onSubmit={handleForgotPasswordSubmit}
    >
      {({ isSubmitting, errors }): JSX.Element => (
        <Form data-testid="requestResetPasswordForm">
          <Card>
            <LoginFormLogo />
            <CardHeader
              data-testid="requestResetPasswordTitle"
              titleTypographyProps={{
                align: "center",
                gutterBottom: false,
                paragraph: false,
                variant: "h1",
              }}
              title={t("passwordManagement.resetPasswordTitle")}
            ></CardHeader>

            <CardContent>
              <Grid
                container
                direction="column"
                alignContent="stretch"
                wrap="nowrap"
                spacing={2}
              >
                <Grid item>
                  <Typography variant="body1" data-testid="resetPasswordText">
                    {t("passwordManagement.resetPassword")}
                  </Typography>
                </Grid>
                <Grid item>
                  <FormInput name="email" label={t("label.email")} />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Typography variant="body1" component="p">
                    <LinkedMessage
                      i18nKey="label.cancel"
                      to="login"
                      testId="cancelResetPasswordRequestLink"
                      gtmEvent="cancelResetPasswordRequestLink"
                    />
                  </Typography>
                </Grid>
                <Grid item>
                  <FormButton
                    waiting={isSubmitting}
                    label={t("button.send")}
                    testId="sendButton"
                    disabled={isCoolingDown}
                  />
                </Grid>
              </Grid>

              {errors.apiResponse && (
                <CardActions>
                  <Alert
                    severity="error"
                    data-testid="generalError"
                    style={{ margin: 10, width: "100%" }}
                  >
                    {errors.apiResponse}
                  </Alert>
                </CardActions>
              )}
            </CardActions>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

export default ForgotPassword;
